// The guest-screen panel on the report page.
//
// Three render paths, picked in this order:
//   1. live=true                           → LiveView (noVNC over wss)
//   2. terminal + replay.mp4 artifact      → <video controls> playing it
//   3. terminal + no replay (yet)          → "Not live" placeholder
//
//   <ScreenPlate jobId live? allowInput? className? />
//
//   useScreenFrame(jobId) → { available: false, … }
//     Kept as a no-op export so the cockpit header's Save-frame
//     button collapses to the right dimmed state without a
//     conditional-import dance; nothing calls save() because the
//     button is also stubbed.
import { LiveBadge, Plate, PlateCap } from "@/components/ui";
import { cx } from "@/components/ui/cx";
import { useArtifacts, useMe, pickArtifact } from "@/hooks";
import { LiveView } from "@/features/live/LiveView";
import { api } from "@/lib/api";

type ScreenPlateProps = { jobId: string; live?: boolean; allowInput?: boolean; saveButton?: boolean; className?: string };

export function useScreenFrame(_jobId: string) {
  return {
    available: false,
    at: null as number | null,
    width: null as number | null,
    height: null as number | null,
    save: () => false,
  };
}

export function ScreenPlate({ jobId, live = false, allowInput, className }: ScreenPlateProps) {
  const me = useMe();
  // Show the Take-control tab only when a signed-in viewer could
  // plausibly be the submitter. The backend's ticket endpoint still
  // checks user_id == submitter_user_id and 403s non-submitters;
  // anonymous jobs (submitter_user_id = NULL) can't be controlled by
  // anyone because there's no identity to gate on.
  const canControl = Boolean(allowInput) && me.data?.authenticated === true;
  // Only fetch artifacts when there's a chance we need the replay —
  // live view runs don't; drop the extra poll by gating on !live.
  const { data: artifacts } = useArtifacts(jobId, { enabled: !live });
  const replay = !live ? pickArtifact(artifacts, "replay") : undefined;
  const replayUrl = replay ? api.artifactURL(jobId, replay.sha256) : undefined;
  return (
    <div className={cx("min-w-0", className)}>
      <Plate live={live}>
        {live ? (
          <LiveView jobId={jobId} canControl={canControl} />
        ) : replayUrl ? (
          <div className="relative w-full overflow-hidden bg-sunk" style={{ aspectRatio: "16 / 10" }}>
            <video
              key={replayUrl}
              className="absolute inset-0 h-full w-full bg-black object-contain"
              controls
              preload="metadata"
              playsInline
              src={replayUrl}
            />
          </div>
        ) : (
          <div className="relative w-full overflow-hidden bg-sunk" style={{ aspectRatio: "16 / 10" }}>
            <div className="absolute inset-0 grid place-items-center p-5 text-center">
              <div className="grid max-w-[52ch] justify-items-center gap-2.5" role="status">
                <p className="font-mono text-key uppercase text-ink-mute">Not live</p>
              </div>
            </div>
          </div>
        )}
      </Plate>
      <PlateCap className="items-center">
        <span className="inline-flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
          {live ? (
            <LiveBadge size="sm" />
          ) : replayUrl ? (
            <span className="font-medium text-ink">Replay</span>
          ) : (
            <span className="font-medium text-ink">Not live</span>
          )}
          <span>job {jobId.slice(0, 8)}</span>
        </span>
      </PlateCap>
    </div>
  );
}
